import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Menu, Moon, Search, Sun, LogOut, Car, Users, Building2, TrendingUp, Key, CheckCheck } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { SidebarContent } from "./Sidebar";
import { fmtDate } from "../utils/format";

export default function Topbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [notifs, setNotifs] = useState({ notifications: [], unread_count: 0 });
  const [openSheet, setOpenSheet] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("ag_theme") === "dark");
  const timer = useRef(null);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("ag_theme", dark ? "dark" : "light");
  }, [dark]);

  const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});

  useEffect(() => {
    loadNotifs();
    const iv = setInterval(loadNotifs, 60000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.length < 2) {
      setResults(null);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(r.data);
        setShowResults(true);
      } catch {}
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  const go = (path) => {
    setShowResults(false);
    setQ("");
    navigate(path);
  };

  const sevColor = { danger: "border-l-red-500", warning: "border-l-amber-500", info: "border-l-sky-500" };

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur-md md:px-6">
      <Sheet open={openSheet} onOpenChange={setOpenSheet}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-64 p-0 border-0">
          <SidebarContent onNavigate={() => setOpenSheet(false)} />
        </SheetContent>
      </Sheet>

      <div className="relative flex-1 max-w-xl">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results && setShowResults(true)}
          onBlur={() => setTimeout(() => setShowResults(false), 200)}
          placeholder="Recherche globale : VIN, immatriculation, client, facture…"
          className="pl-9 bg-slate-50 border-slate-200"
        />
        {showResults && results && (
          <div className="absolute mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-96 overflow-y-auto" data-testid="global-search-results">
            {[
              ["vehicles", "Véhicules", Car, (v) => `/vehicules/${v.id}`, (v) => `${v.brand} ${v.model} — ${v.internal_number}`],
              ["customers", "Clients", Users, (c) => `/clients?open=${c.id}`, (c) => `${c.name} — ${c.phone}`],
              ["suppliers", "Fournisseurs", Building2, (s) => `/fournisseurs?open=${s.id}`, (s) => `${s.name} — ${s.phone}`],
              ["sales", "Ventes", TrendingUp, () => `/ventes`, (s) => `${s.sale_number} — ${s.customer_name}`],
              ["rentals", "Locations", Key, () => `/locations`, (r) => `${r.rental_number} — ${r.customer_name}`],
            ].map(([key, label, Icon, link, txt]) =>
              results[key]?.length ? (
                <div key={key}>
                  <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
                  {results[key].map((item) => (
                    <button key={item.id} onMouseDown={() => go(link(item))} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50" data-testid={`search-result-${key}`}>
                      <Icon className="h-4 w-4 text-slate-400" />
                      <span className="truncate">{txt(item)}</span>
                    </button>
                  ))}
                </div>
              ) : null
            )}
            {!Object.values(results).some((a) => a.length) && (
              <p className="px-3 py-4 text-sm text-slate-400">Aucun résultat pour « {q} »</p>
            )}
          </div>
        )}
      </div>

      <Button variant="ghost" size="icon" onClick={() => setDark((d) => !d)} data-testid="theme-toggle" title={dark ? "Mode clair" : "Mode sombre"}>
        {dark ? <Sun className="h-5 w-5 text-amber-400" /> : <Moon className="h-5 w-5 text-slate-600" />}
      </Button>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell">
            <Bell className="h-5 w-5 text-slate-600" />
            {notifs.unread_count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white" data-testid="notifications-count">
                {notifs.unread_count}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-96 p-0">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <p className="font-heading font-semibold text-sm">Alertes</p>
            <button className="flex items-center gap-1 text-xs text-blue-600 hover:underline" data-testid="mark-all-read-button"
              onClick={async () => { await api.post("/notifications/read-all"); loadNotifs(); }}>
              <CheckCheck className="h-3.5 w-3.5" /> Tout marquer lu
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifs.notifications.length === 0 && <p className="px-4 py-6 text-sm text-slate-400">Aucune alerte.</p>}
            {notifs.notifications.map((n) => (
              <button key={n.id} data-testid={`notification-item-${n.id}`}
                onClick={async () => { await api.post(`/notifications/${n.id}/read`); loadNotifs(); }}
                className={`block w-full border-l-4 px-4 py-3 text-left hover:bg-slate-50 ${sevColor[n.severity] || "border-l-slate-300"} ${n.read ? "opacity-50" : ""}`}>
                <p className="text-sm font-semibold text-slate-800">{n.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                <p className="text-[10px] text-slate-400 mt-1">{fmtDate(n.created_at)}</p>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 rounded-full border border-slate-200 py-1 pl-1 pr-3 hover:bg-slate-50" data-testid="user-menu-button">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" data-testid="user-avatar" />
            ) : (
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0F172A] text-sm font-bold text-cyan-400">
                {(user?.name || "U")[0].toUpperCase()}
              </span>
            )}
            <span className="hidden text-left sm:block">
              <span className="block text-sm font-semibold leading-tight">{user?.name}</span>
              <span className="block text-[11px] capitalize text-slate-500 leading-tight">{user?.role}</span>
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <p className="font-semibold">{user?.name}</p>
            <p className="text-xs font-normal text-slate-500">{user?.email}</p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/profil")} data-testid="menu-my-profile">
            Mon profil
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/profil?tab=securite")} data-testid="menu-security">
            Sécurité
          </DropdownMenuItem>
          {user?.role === "admin" && (
            <DropdownMenuItem onClick={() => navigate("/parametres")} data-testid="menu-settings">
              Paramètres
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-button">
            <LogOut className="mr-2 h-4 w-4" /> Déconnexion
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
